import type { InputDrive, TrucksState, Vec2 } from "@townsy/contracts";
import { TRUCK_DOOR_REACH_METRES, canEnterTruck, stepTruck, truckDoorPoint, truckSeatPoint, wrapAngle, type TruckBody, type TruckEnv, type TruckInput, type TruckMotion } from "@townsy/world";
import { DRIVE_OFFSET_DECAY, DRIVE_SNAP_DISTANCE, DRIVE_SNAP_HEADING } from "../../net/drivePrediction";
import { MAX_PENDING_INPUTS } from "../../net/prediction";
import type { VenueDriver, VenueDrivingModule } from "../types";
import { localTruck } from "./localTruck";
import { asTrucksState, motionOf, truckView } from "./state";

const MAX_SEQ = 2_147_483_647;
const OFFSET_EPS = 1e-4;

function clampUnit(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

export interface TruckDriverOptions {
  vehicleId: string;
  state: TrucksState;
  hz: number;
  /** First sequence number (strictly above everything this visitor sent in this room). */
  firstSeq: number;
  /** Local clock (ms) for the drawn pose (performance.now in the app). */
  now?: () => number;
}

/**
 * Client prediction of the monster truck the local visitor drives (ADR-0016), the same pattern as the street cars'
 * DrivePredictor: every simulation step runs the SAME `stepTruck` as the server with the same environment (the other
 * trucks as last received, the countdown hold); each `input.drive` is kept until the shared state acknowledges it
 * (`lastSeq`), then the prediction rewinds to the server's truck and replays the rest. Small corrections fade out
 * visually (the drawn pose keeps 70% of the error per step: gone in ~150-300 ms); big ones (a race grid placement)
 * snap. While "Get out" is pending or the connection dropped it is held and follows the server.
 *
 * Drawing (ADR-0032): every step goes onto the local truck's even timeline (localTruck.push), which the arena's 3D
 * samples one step in the past. A server state does NOT republish the pose: the smoothed correction keeps the drawn
 * pose where it was, so a state can never restart the drawn motion (which made the truck jump and stall 15 times a
 * second); only a snap starts the timeline over.
 */
export class TruckDriver implements VenueDriver {
  readonly vehicleId: string;
  readonly dt: number;
  state: TruckMotion;
  lastCorrection = 0;
  snaps = 0;
  private shared: TrucksState;
  private pending: { seq: number; input: TruckInput }[] = [];
  private nextSeq: number;
  private lastAck: number;
  private held = false;
  private offset = { x: 0, z: 0, y: 0, heading: 0 };
  private readonly now: () => number;

  constructor(opts: TruckDriverOptions) {
    const view = truckView(opts.state, opts.vehicleId);
    if (!view) throw new Error(`unknown truck ${opts.vehicleId}`);
    this.vehicleId = opts.vehicleId;
    this.dt = 1 / opts.hz;
    this.shared = opts.state;
    this.state = motionOf(view);
    this.lastAck = Math.max(0, Math.floor(view.lastSeq));
    this.nextSeq = Math.min(MAX_SEQ, Math.max(1, Math.floor(opts.firstSeq), this.lastAck + 1));
    this.now = opts.now ?? (() => (typeof performance !== "undefined" ? performance.now() : Date.now()));
    localTruck.reset(this.vehicleId, this.visual, this.now(), this.dt * 1000);
  }

  get isHeld(): boolean {
    return this.held;
  }
  get pendingCount(): number {
    return this.pending.length;
  }
  get acknowledgedSeq(): number {
    return this.lastAck;
  }
  get lastSentSeq(): number {
    return this.nextSeq - 1;
  }

  /** The pose to draw: the prediction plus what is left of the last smoothed correction. */
  get visual(): TruckMotion {
    const o = this.offset;
    return { ...this.state, x: this.state.x + o.x, z: this.state.z + o.z, y: Math.max(0, this.state.y + o.y), heading: wrapAngle(this.state.heading + o.heading) };
  }

  private env(): TruckEnv {
    const others: TruckBody[] = [];
    let held = false;
    for (const t of this.shared.trucks) {
      if (t.id === this.vehicleId) {
        held = t.held === true;
        continue;
      }
      if (t.mode === "returning") continue;
      const m = motionOf(t);
      others.push({ x: m.x, z: m.z, heading: m.heading, y: m.y });
    }
    return { others, held };
  }

  applyInput(intent: { throttle: number; steer: number }): InputDrive | null {
    this.decayOffset();
    if (this.held) {
      this.publish();
      return null;
    }
    const input: TruckInput = { throttle: clampUnit(intent.throttle), steer: clampUnit(intent.steer) };
    this.state = stepTruck(this.state, input, this.dt, this.env());
    const seq = this.nextSeq;
    if (this.nextSeq < MAX_SEQ) this.nextSeq++;
    this.pending.push({ seq, input });
    if (this.pending.length > MAX_PENDING_INPUTS) this.pending.splice(0, this.pending.length - MAX_PENDING_INPUTS);
    this.publish();
    return { seq, throttle: input.throttle, steer: input.steer };
  }

  onShared(shared: unknown): void {
    const st = asTrucksState(shared);
    if (!st) return;
    this.shared = st;
    const v = truckView(st, this.vehicleId);
    if (!v) return;
    const server = motionOf(v);
    if (this.held) {
      if (Number.isFinite(v.lastSeq) && v.lastSeq > this.lastAck) this.lastAck = v.lastSeq;
      this.pending = [];
      this.adopt(server);
      return;
    }
    if (!Number.isFinite(v.lastSeq) || v.lastSeq < this.lastAck) return; // older than what we know
    this.lastAck = v.lastSeq;
    this.pending = this.pending.filter((p) => p.seq > v.lastSeq);
    let s = server;
    if (this.pending.length > 0) {
      const env = this.env();
      for (const p of this.pending) s = stepTruck(s, p.input, this.dt, env);
    }
    this.adopt(s);
  }

  hold(on: boolean): void {
    this.held = on;
    if (on) this.pending = [];
  }

  seat(): { x: number; z: number; facing: number } {
    const v = this.visual;
    const p = truckSeatPoint(v);
    return { x: p.x, z: p.z, facing: v.heading };
  }

  dispose(): void {
    localTruck.clear(this.vehicleId);
  }

  private adopt(next: TruckMotion): void {
    const dx = this.state.x - next.x;
    const dz = this.state.z - next.z;
    const dy = this.state.y - next.y;
    const dh = wrapAngle(this.state.heading - next.heading);
    this.lastCorrection = Math.hypot(dx, dz);
    let snapped = false;
    if (this.lastCorrection > DRIVE_SNAP_DISTANCE || Math.abs(dh) > DRIVE_SNAP_HEADING) {
      this.offset = { x: 0, z: 0, y: 0, heading: 0 };
      this.snaps++;
      snapped = true;
    } else {
      this.offset = { x: this.offset.x + dx, z: this.offset.z + dz, y: this.offset.y + dy, heading: wrapAngle(this.offset.heading + dh) };
      const len = Math.hypot(this.offset.x, this.offset.z);
      if (len > DRIVE_SNAP_DISTANCE) {
        this.offset.x *= DRIVE_SNAP_DISTANCE / len;
        this.offset.z *= DRIVE_SNAP_DISTANCE / len;
      }
    }
    this.state = next;
    // A snap (a teleport onto the race grid) is drawn at once; anything smaller keeps the drawn pose continuous, so
    // the timeline is left alone until the next step.
    if (snapped) localTruck.reset(this.vehicleId, this.visual, this.now(), this.dt * 1000);
  }

  private decayOffset(): void {
    const o = this.offset;
    o.x *= DRIVE_OFFSET_DECAY;
    o.z *= DRIVE_OFFSET_DECAY;
    o.y *= DRIVE_OFFSET_DECAY;
    o.heading *= DRIVE_OFFSET_DECAY;
    if (Math.abs(o.x) < OFFSET_EPS && Math.abs(o.z) < OFFSET_EPS && Math.abs(o.y) < OFFSET_EPS && Math.abs(o.heading) < OFFSET_EPS) this.offset = { x: 0, z: 0, y: 0, heading: 0 };
  }

  /** One simulation step happened (or a held step passed): the drawn timeline gets the pose. */
  private publish(): void {
    localTruck.push(this.vehicleId, this.visual, this.now(), this.dt * 1000);
  }
}

/** The truck at whose driver's door a walker at `pos` stands (stopped, nobody in it), nearest door first. */
export function truckCandidate(shared: unknown, pos: Vec2): string | null {
  const st = asTrucksState(shared);
  if (!st) return null;
  let best: string | null = null;
  let bestDistance = Infinity;
  for (const t of st.trucks) {
    if (t.mode !== "parked" || t.driverActorId) continue;
    const m = motionOf(t);
    if (!canEnterTruck(pos, m, TRUCK_DOOR_REACH_METRES)) continue;
    const door = truckDoorPoint(m);
    const d = Math.hypot(pos.x - door.x, pos.z - door.z);
    if (d < bestDistance) {
      best = t.id;
      bestDistance = d;
    }
  }
  return best;
}

/** The arena's part of the session's driving plumbing (VenueClientModule.driving). */
export const truckDriving: VenueDrivingModule = {
  candidate: truckCandidate,
  startDriver(opts) {
    const st = asTrucksState(opts.shared);
    if (!st || !truckView(st, opts.vehicleId)) return null;
    return new TruckDriver({ vehicleId: opts.vehicleId, state: st, hz: opts.hz, firstSeq: opts.firstSeq });
  },
};
